import React from 'react';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type ConnectAppControlsSettingsType from '@atlassian/jira-spa-apps-project-settings-connect-app-controls/src/index.tsx';
import { markAsProductBundleReady } from '@atlassian/jira-spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@atlassian/jira-spa/src/view/lazy-page/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { type RouteContext, type Match, Redirect } from '@atlassian/react-resource-router';
import AppDiscoverySettingsPage from './app-discovery-settings/index.tsx';

export const LazyConnectAppControlsSettings = lazyForPaint<typeof ConnectAppControlsSettingsType>(
	() =>
		markAsProductBundleReady(
			import(
				/* webpackChunkName: "async-project-settings-connect-app-controls", jiraSpaEntry: "async-project-settings-connect-app-controls" */ '@atlassian/jira-spa-apps-project-settings-connect-app-controls'
			),
		),
);

const ConnectAppsRedirectInner = ({ match, basePath }: { basePath: string; match: Match }) => {
	const { data: projectContext } = useProjectContext();
	const shouldRedirect =
		projectContext != null &&
		projectContext.simplified &&
		projectContext.projectType === SERVICE_DESK_PROJECT;

	return shouldRedirect ? (
		<Redirect
			to={`${basePath}/projects/${String(match.params.projectKey)}/settings/apps/app-fields`}
			push={false}
		/>
	) : (
		<AppDiscoverySettingsPage />
	);
};

export const ConnectAppsRedirect =
	(basePath: string) =>
	({ match }: RouteContext): React.JSX.Element => (
		<ConnectAppsRedirectInner match={match} basePath={basePath} />
	);

const AsyncConnectAppControlsSettings = (): React.JSX.Element => (
	<LazyPage
		Page={LazyConnectAppControlsSettings}
		pageId="project-settings-connect-app-controls"
		shouldShowSpinner={false}
	/>
);

export default AsyncConnectAppControlsSettings;
